import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Comments from "../../common/Comments";
import InfoBox from "../../common/InfoBox";
import Notes from "./Notes";
import DownloadFile from "./DownloadFile";
import { useSelector } from "react-redux";
import AdminComments from "../../common/AdminComments";
import { getVisaDetails } from "../../../services/user.services";
import {
  getStudent,
  retrieveStudentDetails,
} from "../../../services/admin.services";
import {
  MarkSideBarChatNotifications,
  getChatNotifications,
} from "../../../services/user.services";
import {
  IconUser,
  IconPhone,
  IconMail,
  IconAlertCircle,
  IconLoader,
} from "@tabler/icons-react";

const VisaProcess = ({ setMessages }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [blockAccess, setBlockAccess] = useState(false);
  const [studentDetails, setStudentDetails] = useState({});
  const student = useSelector((state) => state.user.userDetails);
  const userId = useSelector((state) => state.user.user_id);
  const location = useLocation();

  const commentsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          markNotificationsAsRead("visa_process");
        }
      },
      { threshold: 0.3 }
    );

    if (commentsRef.current) {
      observer.observe(commentsRef.current);
    }

    return () => {
      if (commentsRef.current) {
        observer.unobserve(commentsRef.current);
      }
    };
  }, []);

  const markNotificationsAsRead = async (title) => {
    try {
      await MarkSideBarChatNotifications();
      const res = await getChatNotifications();
      setMessages(res.data);
    } catch (error) {
      console.error("Failed to mark visa comments as read", error);
    }
  };

  const getVisa = async () => {
    try {
      if (location.pathname.includes("admin")) {
        const id = parseInt(location.pathname.split("/").pop(), 10);
        const res = await getVisaDetails(id);
        setData(res.data);
      } else {
        const res = await getVisaDetails(userId);
        setData(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const Student = async () => {
    const userId = parseInt(location.pathname.split("/").pop(), 10);
    try {
      if (location.pathname.includes("admin")) {
        const res = await retrieveStudentDetails(userId);
        setStudentDetails(res.data);
      } else {
        setStudentDetails(student);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchStudentDetails = async () => {
      await Student();
      getVisa();
    };

    fetchStudentDetails();
  }, []);

  useEffect(() => {
    if (studentDetails?.open_visa_page === false) {
      setBlockAccess(true);
    } else {
      setBlockAccess(false);
    }
  }, [studentDetails]);

  if (blockAccess) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="bg-red-50 text-red-600 p-8 rounded-lg shadow-lg text-center max-w-md animate-fade-in">
          <IconAlertCircle size={48} className="mx-auto mb-4" stroke={1.5} />
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-red-700">
            You are locked from viewing this page. Please contact your counselor
            for more information.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <IconLoader
            size={48}
            className="mx-auto animate-spin text-purple-600"
          />
          <p className="mt-4 text-gray-600 font-medium">
            Loading visa information...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-6">
          {/* Top: Notes and Documents */}
          <div className="w-full">
            <div className="space-y-6">
              {data?.notes?.length > 0 ? (
                <Notes instructions={data} />
              ) : (
                <div className="bg-white rounded-lg shadow-md w-full p-6 border border-gray-200 flex justify-center items-center h-40">
                  <p className="text-gray-500 text-center text-lg font-medium">
                    No notes available
                  </p>
                </div>
              )}

              {data?.documents?.length > 0 ? (
                <DownloadFile instructions={data} />
              ) : (
                <div className="bg-white rounded-lg shadow-md w-full p-6 border border-gray-200 flex justify-center items-center h-40">
                  <p className="text-gray-500 text-center text-lg font-medium">
                    No documents available
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Visa POC Card - above chat, full width or compact */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-purple-100 max-w-md">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
              <h2 className="text-white text-lg font-semibold text-center">
                Visa Point of Contact
              </h2>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center">
                <div className="relative">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                      studentDetails.assigned_admins
                        ?.assigned_visa_manager?.username ||
                        "Unknown User"
                    )}&background=random&bold=true&size=80`}
                    alt="Counselor"
                    className="w-16 h-16 rounded-full border-2 border-purple-200"
                  />
                </div>
                <div className="ml-4">
                  <p className="font-semibold text-gray-800 text-lg">
                    {studentDetails.assigned_admins?.assigned_visa_manager
                      ?.username || "Not Assigned"}
                  </p>
                  <p className="text-gray-500 text-sm">Visa Manager</p>
                </div>
              </div>

              <div className="space-y-2 mt-3">
                <div className="flex items-center text-gray-700">
                  <IconPhone size={18} className="mr-2" stroke={1.5} />
                  <span className="text-sm">
                    {studentDetails.assigned_admins?.assigned_visa_manager
                      ?.phone || "Not Available"}
                  </span>
                </div>
                <div className="flex items-center text-gray-700">
                  <IconMail size={18} className="mr-2" stroke={1.5} />
                  <span className="text-sm overflow-hidden overflow-ellipsis">
                    {studentDetails.assigned_admins?.assigned_visa_manager
                      ?.email || "Not Available"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Visa Chat - below main content, full width */}
          <div ref={commentsRef} className="w-full">
            {location.pathname.includes("admin") ? (
              <AdminComments
                activeTab={{
                  name: "Visa Process",
                  title: "visa_process",
                  count: 3,
                }}
              />
            ) : (
              <Comments />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaProcess;
